import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Lock, CheckCircle2, Play, ArrowLeft, Trophy, Loader2, RefreshCw, Lightbulb, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "sseuropa_progress_v2";
const DEMO_LIMIT_KEY = "sseuropa_demo_ex_count";
const DEMO_LIMIT = 5;
const LEVELS = [
  { id: "A0", title: "A0 — Prima parola", color: "brand-orange-bg" },
  { id: "A1", title: "A1 — Base", color: "brand-blue-bg" },
  { id: "A2", title: "A2 — Elementare", color: "brand-orange-bg" },
  { id: "B1", title: "B1 — Intermedio", color: "brand-blue-bg" },
  { id: "B2", title: "B2 — Autonomo", color: "brand-orange-bg" },
];
const REQUIRED_TO_UNLOCK = 5;

function loadProgress() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
}
function saveProgress(p) { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); }
function getDemoCount() { return parseInt(localStorage.getItem(DEMO_LIMIT_KEY) || "0", 10); }
function bumpDemoCount() {
  const v = getDemoCount() + 1;
  localStorage.setItem(DEMO_LIMIT_KEY, String(v));
  return v;
}

export default function Exercises() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isPremium = user && user.subscription_active;

  const [progress, setProgress] = useState(loadProgress);
  const [activeLevel, setActiveLevel] = useState(null);
  const [ex, setEx] = useState(null);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [demoCount, setDemoCount] = useState(getDemoCount);
  const [paywall, setPaywall] = useState(false);
  const [recentQuestions, setRecentQuestions] = useState([]); // ring buffer of last 8 q strings per session

  const countFor = (id) => progress[id] || 0;
  const isUnlocked = (idx) => idx === 0 || countFor(LEVELS[idx - 1].id) >= REQUIRED_TO_UNLOCK;

  const openLevel = async (levelId) => {
    if (!isPremium && getDemoCount() >= DEMO_LIMIT) {
      setPaywall(true);
      return;
    }
    setActiveLevel(levelId);
    setSelected(null);
    setFeedback(null);
    setShowHint(false);
    await nextExercise(levelId);
  };

  const nextExercise = async (levelId) => {
    if (!isPremium && getDemoCount() >= DEMO_LIMIT) {
      setPaywall(true);
      return;
    }
    setLoading(true);
    setSelected(null);
    setFeedback(null);
    setShowHint(false);
    try {
      const { data } = await axios.post(`${API}/exercises/generate`, {
        level: levelId,
        ui_lang: lang,
        avoid: recentQuestions,
      });
      setEx(data);
      setRecentQuestions((prev) => {
        const next = [...prev, data.q].slice(-8);
        return next;
      });
    } catch (e) { toast.error("Errore nel generare l'esercizio"); }
    finally { setLoading(false); }
  };

  const submit = () => {
    if (selected === null || !ex) return;
    if (selected === ex.answer) {
      setFeedback("correct");
      const p = { ...progress, [activeLevel]: countFor(activeLevel) + 1 };
      saveProgress(p);
      setProgress(p);
      // Increment demo counter for anon users
      let hitLimit = false;
      if (!isPremium) {
        const c = bumpDemoCount();
        setDemoCount(c);
        if (c >= DEMO_LIMIT) hitLimit = true;
      }
      const idx = LEVELS.findIndex((l) => l.id === activeLevel);
      const nextId = LEVELS[idx + 1]?.id;
      if (nextId && p[activeLevel] === REQUIRED_TO_UNLOCK) {
        toast.success(`${t.exercises.levelDone} → ${nextId}`);
      } else {
        toast.success(t.exercises.correct);
      }
      // Auto-advance to next exercise after showing green feedback
      if (!hitLimit) {
        setTimeout(() => nextExercise(activeLevel), 1200);
      }
    } else {
      setFeedback("wrong");
      toast.error(t.exercises.wrong);
    }
  };

  // If the user just finished the demo limit, show paywall after correct answer
  useEffect(() => {
    if (!isPremium && demoCount >= DEMO_LIMIT && activeLevel && feedback === "correct") {
      setTimeout(() => { setPaywall(true); setActiveLevel(null); }, 1500);
    }
  }, [demoCount, feedback, isPremium, activeLevel]);

  if (paywall) {
    return (
      <div className="page-bg py-16 min-h-[80vh] flex items-center justify-center px-4">
        <div className="card-elevated p-8 sm:p-10 max-w-lg w-full text-center fade-up" data-testid="paywall">
          <div className="mx-auto w-16 h-16 rounded-2xl brand-orange-bg text-white flex items-center justify-center mb-4">
            <Sparkles size={30} />
          </div>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0B3D91]">
            Hai completato i 5 esercizi gratuiti!
          </h2>
          <p className="mt-3 text-[#5A6A94]">
            Bravo/a 🎉 Per continuare senza limiti con esercizi <strong>illimitati A0 → B2</strong>,
            corsi Google Meet live e supporto della community, iscriviti con la quota associativa di <strong>€30</strong>{" "}
            (valida 3 mesi).
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="btn-accent" data-testid="paywall-register-btn">Iscriviti ora — €30</Link>
            <Link to="/login" className="btn-outline">Ho già un account</Link>
          </div>
          <button onClick={() => setPaywall(false)} className="mt-6 text-xs text-[#8494B8] underline">Torna ai livelli</button>
        </div>
      </div>
    );
  }

  if (activeLevel) {
    const count = countFor(activeLevel);
    const remaining = isPremium ? null : Math.max(0, DEMO_LIMIT - demoCount);
    return (
      <div className="page-bg py-12 min-h-[80vh]">
        <div className="max-w-2xl mx-auto px-4">
          <button onClick={() => setActiveLevel(null)} className="btn-ghost mb-4" data-testid="exercise-back-btn">
            <ArrowLeft size={16} /> Indietro
          </button>
          {!isPremium && (
            <div className="mb-4 p-3 rounded-xl brand-sky-bg text-sm text-[#0B3D91] text-center" data-testid="demo-banner">
              🎁 Modalità demo — {remaining} esercizi gratuiti rimasti prima di iscriversi
            </div>
          )}
          <div className="card-elevated p-6 sm:p-8 fade-up" data-testid="exercise-card">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-wider text-[#F97316] font-bold">{LEVELS.find((l) => l.id === activeLevel)?.title}</span>
              <span className="text-xs text-[#5A6A94]">{count} completati</span>
            </div>
            {loading ? (
              <div className="py-16 flex flex-col items-center gap-3 text-[#5A6A94]">
                <Loader2 className="animate-spin" size={32} />
                <p className="text-sm">{t.exercises.generating}</p>
              </div>
            ) : ex ? (
              <>
                <h2 className="font-display font-bold text-xl sm:text-2xl text-[#0B3D91] mb-6" data-testid="exercise-question">{ex.q}</h2>
                <div className="space-y-3">
                  {ex.options.map((opt, i) => (
                    <button
                      key={`${ex.q}-${opt}`}
                      onClick={() => setSelected(i)}
                      disabled={feedback === "correct"}
                      data-testid={`exercise-option-${i}`}
                      className={`w-full text-left px-5 py-3.5 rounded-xl border-2 transition-all font-medium ${
                        selected === i
                          ? feedback === "wrong"
                            ? "border-[#F97316] bg-[#FFF4EC]"
                            : "border-[#0B3D91] bg-[#EEF4FF]"
                          : "border-[#DDE4F5] bg-white hover:border-[#A9B7DC]"
                      } ${feedback === "correct" && i === ex.answer ? "!border-[#16A34A] !bg-[#E8F8EE]" : ""}`}
                    >
                      <span className="inline-flex w-6 h-6 rounded-full bg-white border border-[#DDE4F5] items-center justify-center text-xs font-bold mr-3">{String.fromCharCode(65 + i)}</span>
                      {opt}
                    </button>
                  ))}
                </div>
                {ex.hint && showHint && (
                  <div className="mt-4 p-3 rounded-xl brand-sky-bg text-sm text-[#0B3D91]" data-testid="exercise-hint">
                    <span className="font-bold">💡 {t.exercises.hint}: </span>{ex.hint}
                  </div>
                )}
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  {feedback !== "correct" ? (
                    <>
                      <button onClick={submit} disabled={selected === null} className="btn-primary flex-1 !py-3.5 disabled:opacity-50" data-testid="exercise-submit-btn">
                        {t.exercises.checkAnswer}
                      </button>
                      {ex.hint && !showHint && (
                        <button onClick={() => setShowHint(true)} className="btn-outline !py-3.5" data-testid="show-hint-btn">
                          <Lightbulb size={16} />
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="flex-1 py-3.5 text-center text-sm text-[#16A34A] font-semibold flex items-center justify-center gap-2" data-testid="auto-advance-notice">
                      <Loader2 className="animate-spin" size={16} />
                      {t.exercises.correct} — Prossimo esercizio in arrivo...
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  const demoRemaining = Math.max(0, DEMO_LIMIT - demoCount);

  return (
    <div className="py-12 sm:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 fade-up">
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0B3D91]" data-testid="exercises-title">{t.exercises.title}</h1>
          <p className="mt-2 text-[#5A6A94]">{t.exercises.subtitle}</p>
        </div>

        {!isPremium && (
          <div className="card-elevated p-5 mb-8 flex flex-col sm:flex-row items-center gap-4 fade-up bg-gradient-to-br from-white to-[#FFF7E6]" data-testid="demo-status-card">
            <div className="w-12 h-12 rounded-full brand-orange-bg text-white flex items-center justify-center shrink-0"><Sparkles size={22} /></div>
            <div className="flex-1 text-center sm:text-left">
              <div className="text-sm font-semibold text-[#0F1E3D]">Modalità Demo Gratuita</div>
              <div className="text-xs text-[#5A6A94]">
                {demoRemaining > 0 ? (
                  <>Hai <strong>{demoRemaining}</strong> esercizi gratuiti su {DEMO_LIMIT}. Iscriviti per esercizi illimitati.</>
                ) : (
                  <>Hai finito i 5 esercizi gratuiti. <strong>Iscriviti</strong> per continuare senza limiti.</>
                )}
              </div>
            </div>
            <Link to="/register" className="btn-accent !py-2 !px-4 text-sm" data-testid="demo-signup-btn">Iscriviti — €30</Link>
          </div>
        )}

        {isPremium && (
          <div className="card-elevated p-5 mb-8 flex items-center gap-4 fade-up" data-testid="premium-badge">
            <div className="w-12 h-12 rounded-full brand-blue-bg text-white flex items-center justify-center"><Trophy size={22} /></div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#0F1E3D]">Iscrizione attiva ✨</div>
              <div className="text-xs text-[#5A6A94]">Esercizi illimitati fino a {user.subscription_end && new Date(user.subscription_end).toLocaleDateString("it-IT")}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {LEVELS.map((lvl, i) => {
            const unlocked = isUnlocked(i);
            const cnt = countFor(lvl.id);
            const pct = Math.min(100, Math.round((cnt / REQUIRED_TO_UNLOCK) * 100));
            return (
              <div key={lvl.id} className={`card-elevated p-6 fade-up ${!unlocked ? "opacity-70" : ""}`} style={{ animationDelay: `${i * 0.05}s` }} data-testid={`level-card-${lvl.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-11 h-11 rounded-xl ${lvl.color} text-white flex items-center justify-center font-bold`}>{lvl.id}</div>
                  {!unlocked ? <Lock size={20} className="text-[#8494B8]" data-testid={`locked-${lvl.id}`} /> : cnt >= REQUIRED_TO_UNLOCK ? <CheckCircle2 size={20} className="text-[#16A34A]" /> : null}
                </div>
                <h3 className="font-display font-bold text-lg text-[#0B3D91]">{lvl.title}</h3>
                <p className="text-xs text-[#5A6A94] mt-1 mb-4">{cnt} completati · {cnt >= REQUIRED_TO_UNLOCK ? "sbloccato ✓" : `${REQUIRED_TO_UNLOCK - cnt} per sbloccare il prossimo`}</p>
                <div className="h-1.5 rounded-full bg-[#EEF4FF] overflow-hidden mb-4">
                  <div className={`h-full ${lvl.color}`} style={{ width: `${pct}%` }} />
                </div>
                <button
                  onClick={() => unlocked && openLevel(lvl.id)}
                  disabled={!unlocked}
                  className={`w-full ${unlocked ? "btn-primary" : "btn-outline"} !py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`start-${lvl.id}`}
                >
                  {!unlocked ? <><Lock size={14} /> {t.exercises.locked}</> : <><Play size={14} /> {cnt > 0 ? t.exercises.nextExercise : t.exercises.start}</>}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
